import React from 'react';
import FaqList from '../../faq/FaqList';
import './productfaq.css';

// Builds product-specific questions from the product data in src/content/products.
// A question is only shown when the data needed to answer it exists, so the answers
// always match the specifications on the page.

const BALE_WEIGHT_LABELS = {
    cardboard: 'kartong',
    plastic: 'plast',
    textilier: 'textilier',
    plastic_bottles: 'PET-flaskor',
    aluminium_cans: 'aluminiumburkar',
    steel_drums: 'stålfat',
};

const hasValue = (value) => value !== undefined && value !== null && String(value).trim() !== '' && value !== 'SAKNAS';

const joinList = (list) => {
    if (list.length <= 1) return list.join('');
    return `${list.slice(0, -1).join(', ')} och ${list[list.length - 1]}`;
};

const MATERIAL_NAMES = {
    'pet flaskor': 'PET-flaskor',
    'aluminium burkar': 'aluminiumburkar',
    'metall burkar': 'metallburkar',
    'eps skumplast': 'EPS-skumplast',
    'blandat': 'blandat avfall',
    'organiskt': 'organiskt avfall',
    'flytande': 'flytande avfall',
    'trummor': 'fat och trummor',
};

// "Wellpapp" -> "wellpapp", but keep abbreviations such as "EPP / XPS Cellplast".
const toMaterialName = (material) => {
    const known = MATERIAL_NAMES[material.toLowerCase()];
    if (known) return known;
    const firstWord = material.split(' ')[0];
    return firstWord === firstWord.toUpperCase() ? material : material.charAt(0).toLowerCase() + material.slice(1);
};

const varies = (value) => /varierar/i.test(String(value));

export const buildProductFaq = (product) => {
    const name = product.namn;
    const isBaler = product.typ === 'Balpressar';
    const isCompactor = product.typ === 'Komprimatorer';
    const items = [];

    const materials = (product.avfallstyp || []).filter(hasValue);
    if (materials.length) {
        let answer = `Med ${name} kan ni hantera ${joinList(materials.map(toMaterialName))}.`;
        if (/galvaniserad/i.test(product.id)) {
            answer += ' Det galvaniserade utförandet gör maskinen lämplig för våta och krävande miljöer.';
        }
        if (isBaler) {
            answer += ' Pressa en fraktion i taget, så blir balarna renare och lättare att lämna till återvinning.';
        }
        items.push({ question: `Vilka material kan ${name} hantera?`, answer });
    }

    const baleWeights = Object.entries(product.balvikt || {})
        .filter(([key, value]) => BALE_WEIGHT_LABELS[key] && hasValue(value))
        .map(([key, value]) => `${value} kg för ${BALE_WEIGHT_LABELS[key]}`);
    if (isBaler && baleWeights.length) {
        let answer = `En färdig bal från ${name} väger ungefär ${joinList(baleWeights)}.`;
        if (hasValue(product.balstorlek)) {
            answer += ` Balstorleken är ${product.balstorlek} mm (B x D x H).`;
        }
        answer += ' Den exakta vikten beror på materialet och hur torrt och rent det är.';
        items.push({ question: `Hur tunga och stora blir balarna från ${name}?`, answer });
    }

    if (hasValue(product.presstryck) && (isBaler || isCompactor)) {
        const effect = isBaler
            ? 'Ett högre presstryck ger tätare och tyngre balar, vilket innebär färre hämtningar.'
            : 'Ett högre presstryck ger mer avfall per tömning, vilket innebär färre hämtningar.';
        const answer = varies(product.presstryck)
            ? `Presstrycket för ${name} varierar beroende på vilken modell ni väljer. Kontakta oss så hjälper vi er att hitta rätt modell för era volymer.`
            : `${name} har ett presstryck på ${product.presstryck} ton. ${effect}`;
        items.push({ question: `Vilket presstryck har ${name}?`, answer });
    }

    if (hasValue(product.strömförsörjning)) {
        const power = product.strömförsörjning;
        let answer;
        if (varies(power)) {
            answer = `Strömförsörjningen för ${name} varierar beroende på modell. Vi går igenom elanslutningen tillsammans med er inför installationen.`;
        } else if (/^1x230V/i.test(power)) {
            answer = `${name} ansluts till ${power}, alltså enfas 230 V. Ingen trefasanslutning behövs, vilket gör maskinen enkel att placera.`;
        } else if (/^3x400V/i.test(power)) {
            answer = `${name} kräver trefasanslutning: ${power}. Se till att det finns ett trefasuttag där maskinen ska stå, så hjälper vi er med placeringen vid installationen.`;
        } else {
            answer = `${name} ansluts till ${power}.`;
        }
        items.push({ question: `Vilken elanslutning behöver ${name}?`, answer });
    }

    if (hasValue(product.mått)) {
        let answer = `${name} mäter ${product.mått} mm (B x D x H)`;
        answer += hasValue(product.vikt) ? ` och väger ${product.vikt} kg.` : '.';
        if (hasValue(product.fyllningsöppning)) {
            answer += ` Fyllningsöppningen är ${product.fyllningsöppning} mm.`;
        }
        answer += isBaler
            ? ' Räkna med fri yta runt maskinen så att personalen kan arbeta säkert och ta ut balarna smidigt.'
            : ' Räkna med fri yta runt maskinen så att personalen kan arbeta säkert.';
        items.push({ question: `Hur mycket plats tar ${name}?`, answer });
    }

    if (hasValue(product.bindningstyp) || hasValue(product.utbalning)) {
        const parts = [];
        if (hasValue(product.antal_band) && hasValue(product.bindningstyp)) {
            parts.push(`Balarna binds med ${product.antal_band} band (${product.bindningstyp.toLowerCase()}).`);
        } else if (/automatisk/i.test(product.bindningstyp || '')) {
            parts.push('Bindningen av balarna sker automatiskt.');
        } else if (hasValue(product.bindningstyp)) {
            parts.push(`Balarna binds med ${product.bindningstyp.toLowerCase()}.`);
        }
        if (hasValue(product.utbalning)) {
            parts.push(/automatisk/i.test(product.utbalning)
                ? 'Utbalningen är automatisk, vilket gör det lätt och ergonomiskt att få ut den färdiga balen.'
                : 'Utbalningen sker manuellt.');
        }
        if (product.ljus_vid_full_bal === 'Ja') {
            parts.push('En indikator visar när det är dags att binda en bal.');
        }
        items.push({ question: `Hur binds och tas balarna ut ur ${name}?`, answer: parts.join(' ') });
    }

    if (isBaler || isCompactor) {
        const category = isBaler ? 'balpressar' : 'komprimatorer';
        items.push({
            question: `Kan vi hyra ${name} i stället för att köpa?`,
            answer: `Vi erbjuder både köp och hyra av ${category}. [Kontakta oss](/kontakt) så går vi igenom era volymer och tar fram ett förslag som passar er verksamhet och budget.`,
        });
    }

    items.push({
        question: `Ingår installation och service när vi köper ${name}?`,
        answer: 'När ni köper utrustning från oss erbjuder vi installation, användarutbildning och servicepaket. Med ett serviceavtal minskar risken för driftstopp. Läs mer om våra [serviceavtal](/service).',
    });

    return items;
};

const ProductFaq = ({ product }) => {
    const items = buildProductFaq(product);

    return (
        <section className="product-faq">
            <h2>Vanliga frågor om {product.namn}</h2>
            <FaqList items={items} />
        </section>
    );
};

export default ProductFaq;
